import React, { useEffect, useState } from "react";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormControl, MenuItem, Select } from "@mui/material";
import { PropertyObj } from "../../../Models/PropertyObj";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import EnergyPerformanceBar from "./energy-performance-bar ";
dayjs.extend(customParseFormat);
dayjs.locale("fr");

const Country: React.FunctionComponent<{ property: any, errors: any, onchangeProperty: any, country: string }> = (props) => {

    const [property, setProperty] = useState<PropertyObj>(props.property ? props.property : {});
    const { t } = useTranslation();
    const epdLevels = [
        { label: "A", color: "#008000" },
        { label: "B", color: "#5cb85c" },
        { label: "C", color: "#d4e157" },
        { label: "D", color: "#fdd835" },
        { label: "E", color: "#fbc02d" },
        { label: "F", color: "#f57c00" },
        { label: "G", color: "#e64a19" }
    ];
    const gesLevels = [
        { label: "A", color: "#a2dfff" },
        { label: "B", color: "#7ecdf7" },
        { label: "C", color: "#5ca6cc" },
        { label: "D", color: "#496a9a" },
        { label: "E", color: "#3e4e76" },
        { label: "F", color: "#2f3a5a" },
        { label: "G", color: "#1e1231" }
    ];
    // Configuration for different countries
    const [countryConfig] = useState<any>({
        "UK": {
            "property_type": ["Bungalows", "Detached", "Farms/land", "Semi-detached", "Terraced", "houses", "Flats"],
            "price_currency": ['GBP', 'USD', 'EUR'],
            "fields": ["Permit", "buildYear", "availableForm", "deliveryDate", "paymentCheques", "floor", "parking", "QrCode"]
        },
        "UAE": {
            "property_type": ["Apartment", "Loft Apartment", "Hotel Apartment", "Villa", "Villa Compound", "Full Floor Residential", "Land Residential", "Residential Building", "Townhouse", "Penthouse", "Duplex", "Bulk Units", "Bungalow", "Multiple Sale Units", "Shop", "Office"],
            "price_currency": ['AED', 'SAR', 'USD', 'EUR'],
            "fields": ["titleDeed", "Permit", "buildYear", "availableForm", "deliveryDate", "paymentCheques", "floor", "parking", "QrCode"]
        },
        "ME": {
            "property_type": ["Apartment", "New building", "Land", "Villa", "House", "Hotel", "Commercial real estate"],
            "price_currency": ["EUR"],
            "fields": ["floor", "buildYear", "parking", "view"]
        },
        "FR": {
            "property_type": ["Apartment", "Villa", "House", "Hotel", "Townhouse", "Penthouse", "Duplex"],
            "price_currency": ["EUR"],
            "fields": ["rooms", "floor", "parking", "buildYear", "EPD", "GHG", "energySources", "heatingType"
            ]
        }
    });

    useEffect(() => {
        props.onchangeProperty(property)
    }, [property])

    const handleChange = (event: any) => {
        const { name, value } = event.target;
        setProperty((prev: any) => ({ ...prev, [name]: value }));
    };

    const formatDate = (dateString: string) => {
        return dayjs(dateString).format("DD/MM/YYYY");
    };

    const handleAvailableDateChange = (newValue: any) => {
        setProperty((prevOptions: any) => ({
            ...prevOptions,
            availableForm: formatDate(newValue),
        }));
    };

    const handleDeliveryDateChange = (newValue: any) => {
        setProperty((prevOptions: any) => ({
            ...prevOptions,
            deliveryDate: formatDate(newValue),
        }));
    };

    const handleChangeQrCode = (event: any) => {
        setProperty((prevOptions: any) => ({
            ...prevOptions,
            QrCode: event.target.checked,
        }));
    }

    // Get current country configuration
    const currentConfig = countryConfig[props.country] || countryConfig["UAE"]; // Default to UAE if country not found
    const allowedFields = currentConfig.fields || [];

    // Helper function to check if field should be displayed
    const shouldShowField = (fieldName: string) => {
        return allowedFields.includes(fieldName);
    };

    return (
        <div className="row card-form mb-4">
            {/* Title Deed - Only for UAE */}
            {shouldShowField("titleDeed") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.titleDeed")} </label>
                    <input type="text" className=" form-input" placeholder={`${t("propertyDetails.titleDeed")} (${t("propertyDetails.optional")})`} value={property.titleDeed || ''} name="titleDeed" onChange={handleChange} />
                </div>
            )}

            {/* Permit - UAE and UK */}
            {shouldShowField("Permit") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.Permit")}
                        
                    </label>
                    <input type="number" id="Permit" min={0}
                        className={props.country === "UAE" ? `form-input`
                            : 'form-input'}
                        placeholder={
                            props.country !== "UAE"
                                ? `${t("propertyDetails.Permit")} (${t("propertyDetails.optional")})`
                                : t("propertyDetails.Permit")}
                        value={property.Permit || ''} name="Permit" onChange={handleChange} />
                </div>
            )}

            {shouldShowField("rooms") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("generateDescTitle.rooms")}</label>
                    <input type="number" id="rooms " className=" form-input" min={0} placeholder={`${t("generateDescTitle.rooms")} (${t("propertyDetails.optional")})`} value={property.rooms || ''} name="rooms" onChange={handleChange} />
                </div>
            )}

            {/* Build Year - All countries */}
            {shouldShowField("buildYear") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.buildYear")}</label>
                    <input type="number" id="Build Year " className=" form-input" min={0} placeholder={`${t("propertyDetails.buildYear")} (${t("propertyDetails.optional")})`} value={property.buildYear || ''} name="buildYear" onChange={handleChange} />
                </div>
            )}

            {/* Available Form - UAE and UK */}
            {shouldShowField("availableForm") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt"> {t("propertyDetails.availableForm")}  </label>
                    <FormControl fullWidth sx={{ width: "100%" }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker']} sx={{
                                borderRadius: "10px",
                                background: "#FFFFFF",
                                width: "100%"
                            }}>
                                <DatePicker value={property.availableForm ? dayjs(property.availableForm) : null} onChange={handleAvailableDateChange}
                                    sx={{
                                        width: "100%",
                                        '.MuiInputBase-root input': {
                                            border: 'none'
                                        },
                                        '.MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        },

                                    }} />
                            </DemoContainer>
                        </LocalizationProvider>
                    </FormControl>
                </div>
            )}

            {/* Delivery Date - UAE and UK */}
            {shouldShowField("deliveryDate") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.deliveryDate")} </label>
                    <FormControl fullWidth>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker']} sx={{ borderRadius: "10px", background: "#FFFFFF" }}>
                                <DatePicker value={property.deliveryDate ? dayjs(property.deliveryDate) : null} onChange={handleDeliveryDateChange}
                                    sx={{
                                        width: "100%",
                                        '.MuiInputBase-root input': {
                                            border: 'none'
                                        },
                                        '.MuiOutlinedInput-notchedOutline': {
                                            border: 'none'
                                        }
                                    }} />
                            </DemoContainer>
                        </LocalizationProvider>
                    </FormControl>
                </div>
            )}

            {/* Payment Cheques - UAE and UK */}
            {shouldShowField("paymentCheques") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.paymentCheques")}</label>
                    <FormControl fullWidth>
                        <Select
                            className="input-property"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.paymentCheques || ''}
                            name="paymentCheques"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled> {t("propertiesPage.select")}  </MenuItem>
                            <MenuItem value={"1"}>1</MenuItem>
                            <MenuItem value={"2"}>2</MenuItem>
                            <MenuItem value={"3"}>3</MenuItem>
                            <MenuItem value={"4"}>4</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}

            {/* Floor - All countries */}
            {shouldShowField("floor") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.floor")} </label>
                    <input type="number" id="Floor" className=" form-input" min={0} placeholder={`${t("propertyDetails.floor")} (${t("propertyDetails.optional")})`} value={property.floor || ''} name="floor" onChange={handleChange} />
                </div>
            )}

            {/* Parking - All countries */}
            {shouldShowField("parking") && (
                <div className="col-md-4 mb-3">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.parking")} </label>
                    <input type="number" className=" form-input" min={0} placeholder={`${t("propertyDetails.parking")} (${t("propertyDetails.optional")})`} value={property.parking || ''} name="parking" onChange={handleChange} />
                </div>
            )}

            {/* View - Only for ME */}
            {shouldShowField("view") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("propertyDetails.view")}  </label>
                    <FormControl fullWidth>
                        <Select
                            className="input-property"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select View' }}
                            value={property.view || ''}
                            name="view"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled> {t("propertyDetails.selectView")} </MenuItem>
                            <MenuItem value={"Sea view"}>{t("propertyDetails.seaView")}</MenuItem>
                            <MenuItem value={"Mountain view"}>{t("propertyDetails.mountainView")}</MenuItem>
                            <MenuItem value={"City view"}>{t("propertyDetails.cityView")}</MenuItem>
                        </Select>
                    </FormControl>
                </div>
            )}

            {/* QR Code - UAE and UK */}
            {shouldShowField("QrCode") && (
                <div className="col-md-4 mb-3 d-flex  align-items-end">
                    <div className="form-check mb-2">
                        <input className="form-check-input" type="checkbox" onChange={handleChangeQrCode} checked={property.QrCode ? property.QrCode : ""} />
                        <label className="form-check-label" htmlFor="defaultCheckQR" style={{ cursor: "pointer" }}>
                            {t("propertyDetails.createQrCode")}
                        </label>
                    </div>
                </div>
            )}


            {shouldShowField("energySources") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("generateDescTitle.energySources")} </label>
                    <input type="text" className=" form-input" placeholder={`${t("generateDescTitle.energySources")} (${t("propertyDetails.optional")})`} value={property.energySources || ''} name="energySources" onChange={handleChange} />
                </div>
            )}

            {shouldShowField("heatingType") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("generateDescTitle.heatingType")} </label>
                    <input type="text" className=" form-input" placeholder={`${t("generateDescTitle.heatingType")} (${t("propertyDetails.optional")})`} value={property.heatingType || ''} name="heatingType" onChange={handleChange} />
                </div>
            )}

            {shouldShowField("EPD") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("generateDescTitle.EPD")}  </label>
                    <FormControl fullWidth>
                        <Select
                            className="input-property mb-3"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.EPD || ''}
                            name="EPD"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled> {t("propertiesPage.select")} </MenuItem>
                            <MenuItem value={"A"}>A</MenuItem>
                            <MenuItem value={"B"}>B</MenuItem>
                            <MenuItem value={"C"}>C</MenuItem>
                            <MenuItem value={"D"}>D</MenuItem>
                            <MenuItem value={"E"}>E</MenuItem>
                            <MenuItem value={"F"}>F</MenuItem>
                            <MenuItem value={"G"}>G</MenuItem>
                        </Select>
                    </FormControl>
                    <EnergyPerformanceBar selected={property.EPD} epdLevels={epdLevels} />

                </div>
            )}

            {shouldShowField("GHG") && (
                <div className="col-md-4 mb-3 ">
                    <label className="form-label-1 mb-2" htmlFor="selectTypeOpt">{t("generateDescTitle.GHG")}  </label>
                    <FormControl fullWidth>
                        <Select
                            className="input-property mb-3"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={property.GHG || ''}
                            name="GHG"
                            onChange={handleChange}
                        >
                            <MenuItem value="" disabled> {t("propertiesPage.select")} </MenuItem>
                            <MenuItem value={"A"}>A</MenuItem>
                            <MenuItem value={"B"}>B</MenuItem>
                            <MenuItem value={"C"}>C</MenuItem>
                            <MenuItem value={"D"}>D</MenuItem>
                            <MenuItem value={"E"}>E</MenuItem>
                            <MenuItem value={"F"}>F</MenuItem>
                            <MenuItem value={"G"}>G</MenuItem>
                        </Select>
                    </FormControl>
                    <EnergyPerformanceBar selected={property.GHG} epdLevels={gesLevels} />
                </div>
            )}
        </div>
    )
}

export default Country;